import React, { useState, useEffect } from "react";
import AOS from 'aos';
import 'aos/dist/aos.css';

function FAQItem({ question, answer, isOpen, onClick }) {
  return (
    <div className="border-b border-gray-300 mb-4">
      <div
        className="flex justify-between items-center py-4 cursor-pointer hover:bg-gray-200 transition duration-200 ease-in-out"
        onClick={onClick}
      >
        <span className="text-lg font-semibold text-gray-800">{question}</span>
        <i
          className={`fas fa-chevron-${isOpen ? "up" : "down"} text-gray-600`}
        ></i>
      </div>
      {isOpen && <div className="py-2 text-gray-700">{answer}</div>}
    </div>
  );
}

export default function WebDevelopment() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [openIndex, setOpenIndex] = useState(null);
  const [data, setData] = useState({
    faqs: [],
    services: [],
    testimonials: [],
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [faqs, services, testimonials] = await Promise.all([
          fetch("./Database/Webfaqs.json").then((res) => res.json()),
          fetch("./Database/WebServices.json").then((res) => res.json()),
          fetch("./Database/WebTestimonials.json").then((res) => res.json()),
        ]);
        setData({ faqs, services, testimonials });
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
    AOS.init(); // Initialize AOS
  }, []);

  const handleClick = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const handlePrevClick = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? data.testimonials.length - 1 : prevIndex - 1
    );
  };

  const handleNextClick = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === data.testimonials.length - 1 ? 0 : prevIndex + 1
    );
  };

  return (
    <div>
      <section className="text-center py-20 mt-10 flex-wrap" data-aos="fade-up">
        <h1 className="text-5xl font-bold mb-6">
          Website Design and Development Services with the Best Agency
        </h1>
        <p className="text-lg mb-8">
          Is your website confusing visitors instead of converting them? With
          the right mix of user experience and stunning design, you’ll ignite
          growth. Let us take you there.
        </p>
        <button className="bg-yellow-400 text-black font-bold py-3 px-6 rounded-full">
          Build Your Website Now
        </button>
      </section>

      {/* Trusted Brands Section */}
      <section className="bg-gray-100 py-10" data-aos="fade-up">
        <div className="container mx-auto text-center">
          <h2 className="text-sm font-bold mb-6 text-gray-600">
            TRUSTED BY 200+ GLOBAL BRANDS
          </h2>
          <div className="marquee">
            {[...Array(10)].map((_, i) => (
              <img
                key={i}
                src="https://placehold.co/100x50"
                alt={`Brand logo ${i + 1}`}
                className="h-10 mx-2"
              />
            ))}
          </div>
        </div>
      </section>

      {/* Responsive Design Section */}
      <section className="bg-white py-16 px-4" data-aos="fade-right">
        <div className="container mx-auto flex flex-col md:flex-row items-center">
          <div className="md:w-1/2">
            <img
              src="https://placehold.co/400x300"
              alt="Responsive design examples on various devices"
              className="w-full"
            />
          </div>
          <div className="md:w-1/2 md:pl-10 mt-10 md:mt-0">
            <h2 className="text-3xl font-bold mb-4">
              Ready to reach more and make a bigger impact? Go responsive now!
            </h2>
            <p className="text-lg mb-4">
              If your website doesn't adapt to any device or screen size, you're
              losing a huge audience. We guarantee your design fits perfectly,
              every time, on every display.
            </p>
            <button className="bg-yellow-400 text-black font-bold py-2 px-4 rounded">
              Schedule a call now
            </button>
          </div>
        </div>
      </section>

      {/* Featured Case Study Section */}
      <section className="bg-black text-white py-16 px-4" data-aos="fade-left">
        <div className="container mx-auto flex flex-col md:flex-row items-center">
          <div className="md:w-1/2">
            <h2 className="text-4xl font-bold mb-4">Featured Case Study</h2>
            <div className="relative">
              <img
                src="https://placehold.co/400x300"
                alt="Nuport Website on a laptop screen"
                className="w-full"
              />
            </div>
          </div>
          <div className="md:w-1/2 md:pl-10 mt-10 md:mt-0">
            <h3 className="text-2xl font-bold mb-2">Nuport Website</h3>
            <p className="text-lg mb-4">
              Collaboratively our contrary companion and helps you navigate the
              with an attorney. Users can share information with collections for
              prepared communication of cases.
            </p>
            <p className="text-lg mb-4">
              We redesigned the entire app, website, showreel, branding, Motion
              graphics.
            </p>
            <button className="bg-yellow-400 text-black font-bold py-2 px-4 rounded">
              Check full case study
            </button>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <div className="container mx-auto px-4 py-8" data-aos="fade-up">
        <h1 className="text-3xl font-bold text-center mb-8">
          Achieve success with our all-in-one web design and development
          services
        </h1>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {data.services.map((service, index) => (
            <div
              key={index}
              className="text-center bg-gray-50 p-4 rounded-lg shadow-lg"
              data-aos="zoom-in"
            >
              <img
                src={service.image}
                alt={service.alt}
                className="w-full h-48 object-cover mb-4 rounded"
              />
              <h2 className="text-xl font-bold mb-2">{service.title}</h2>
              <p className="text-gray-700">{service.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ Section */}
      <div className="bg-gray-100 py-16 px-4" data-aos="fade-up">
        <div className="container mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8 text-gray-800">
            Frequently Asked Questions
          </h2>
          <div className="max-w-2xl mx-auto">
            {data.faqs.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openIndex === index}
                onClick={() => handleClick(index)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
